/**
 * Import Monitor Health Check tab.
 *
 * CR-5318: this tab is the new "Exceptions" view — it surfaces every recent
 * import job that failed or partially failed so an admin can see at a glance
 * which rows didn't make it in, click through to the per-row error log, and
 * triage. The on-demand integrity-check runner (currently just the GRN
 * attribution check) lives below as a secondary section.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, Play,
  AlertCircle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  importsAPI,
  type HealthCheckDescriptor,
  type HealthCheckResponse,
  type HealthCheckStatus,
  type ImportJob,
} from '../../lib/api/imports';
import { failedBeforeReading, failedCountText } from './importJobText';

interface ImportHealthCheckTabProps {
  /** Customer id to scope checks against. */
  customerId: number;
  /** Recent failed/partial jobs from the dashboard summary. */
  recentFailures: ImportJob[];
  /** Total job-level failures today. */
  jobsFailedToday: number;
  /** Sum of failed rows across today's jobs. */
  recordsFailedToday: number;
  /** Open the per-job drill-down modal so admins can see the per-row errors. */
  onSelectJob: (jobId: string) => void;
}

const STATUS_STYLES: Record<HealthCheckStatus, { className: string; Icon: React.ComponentType<{ className?: string }>; label: string }> = {
  pass: { className: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2, label: 'Pass' },
  warning: { className: 'text-amber-700 bg-amber-50 border-amber-200', Icon: AlertTriangle, label: 'Warning' },
  fail: { className: 'text-red-700 bg-red-50 border-red-200', Icon: XCircle, label: 'Fail' },
  error: { className: 'text-red-700 bg-red-50 border-red-200', Icon: XCircle, label: 'Error' },
};

export default function ImportHealthCheckTab({
  customerId,
  recentFailures,
  jobsFailedToday,
  recordsFailedToday,
  onSelectJob,
}: ImportHealthCheckTabProps) {
  const scopedCustomerId = customerId;

  const [checks, setChecks] = useState<HealthCheckDescriptor[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, HealthCheckResponse>>({});

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    importsAPI
      .listHealthChecks()
      .then(data => {
        if (!cancelled) setChecks(data);
      })
      .catch(err => {
        if (!cancelled) toast.error(`Failed to load health checks: ${err?.message ?? 'unknown'}`);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runCheck = useCallback(
    async (checkId: string) => {
      if (!scopedCustomerId) {
        toast.error('No customer scope available — cannot run check.');
        return;
      }
      setRunning(prev => ({ ...prev, [checkId]: true }));
      try {
        const res = await importsAPI.runHealthCheck(checkId, scopedCustomerId);
        setResults(prev => ({ ...prev, [checkId]: res }));
        const style = STATUS_STYLES[res.status] ?? STATUS_STYLES.error;
        if (res.status === 'pass') toast.success(`${checkId}: ${style.label}`);
        else toast.error(`${checkId}: ${style.label} — ${res.summary}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error(`Check ${checkId} failed: ${msg}`);
      } finally {
        setRunning(prev => ({ ...prev, [checkId]: false }));
      }
    },
    [scopedCustomerId],
  );

  const totalFailedRecords = recentFailures.reduce((sum, j) => sum + (j.records_failed || 0), 0);

  return (
    <div className="space-y-6">
      {/* Exceptions summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border rounded-lg p-3 bg-red-50/50 border-red-200">
          <div className="text-xs text-red-700 uppercase font-semibold">Failed Today</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{jobsFailedToday}</div>
          <div className="text-xs text-red-700/80 mt-0.5">
            {recordsFailedToday.toLocaleString()} record{recordsFailedToday === 1 ? '' : 's'}
          </div>
        </div>
        <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
          <div className="text-xs text-amber-700 uppercase font-semibold">Recent Exception Jobs</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{recentFailures.length}</div>
          <div className="text-xs text-amber-700/80 mt-0.5">
            {totalFailedRecords.toLocaleString()} failed record{totalFailedRecords === 1 ? '' : 's'} total
          </div>
        </div>
        <div className="border rounded-lg p-3 bg-slate-50 border-slate-200">
          <div className="text-xs text-slate-700 uppercase font-semibold">Integrity Checks</div>
          <div className="text-2xl font-bold text-slate-700 mt-1">{checks.length}</div>
          <div className="text-xs text-slate-700/80 mt-0.5">on-demand diagnostic{checks.length === 1 ? '' : 's'}</div>
        </div>
      </div>

      {/* Recent exceptions list */}
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div>
            <h3 className="font-medium text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              Import Exceptions
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Jobs that failed or partially failed. Click any row to inspect the per-row error log.
            </p>
          </div>
        </div>

        {recentFailures.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-10">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
            No import exceptions recorded for this source.
          </div>
        ) : (
          <div className="divide-y">
            {recentFailures.map((job) => {
              const completedDate = job.completed_at || job.started_at || job.created_at;
              return (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => onSelectJob(job.job_id)}
                  className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-muted/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{job.data_type_display}</span>
                      <Badge
                        variant="outline"
                        className={
                          job.status === 'failed'
                            ? 'text-red-700 border-red-300 bg-red-50 text-[10px]'
                            : 'text-amber-700 border-amber-300 bg-amber-50 text-[10px]'
                        }
                      >
                        {job.status_display}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{job.customer_name}</span>
                    </div>
                    {job.error_message && (
                      <p className="text-xs text-red-700 line-clamp-2 font-mono">
                        {job.error_message}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      {failedBeforeReading(job) ? (
                        <span>{failedCountText(job)}</span>
                      ) : (
                        <span>
                          <span className="font-semibold text-red-700">{job.records_failed.toLocaleString()}</span>
                          <span> failed of </span>
                          <span>{job.total_records.toLocaleString()}</span>
                        </span>
                      )}
                      {completedDate && (
                        <span>
                          {formatDistanceToNow(new Date(completedDate), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Integrity checks (on-demand diagnostics) */}
      <div className="border rounded-lg">
        <div className="px-4 py-3 border-b bg-muted/30">
          <h3 className="font-medium text-sm">Integrity Checks</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            On-demand diagnostics that scan the imported data for structural problems.
            Each check runs synchronously and returns metrics + sample violations.
          </p>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : checks.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            No integrity checks registered.
          </div>
        ) : (
          <div className="p-3 space-y-3">
            {checks.map(check => {
              const result = results[check.id];
              const isRunning = !!running[check.id];
              const style = result ? STATUS_STYLES[result.status] ?? STATUS_STYLES.error : null;
              return (
                <div key={check.id} className="border rounded-lg overflow-hidden">
                  <div className="flex items-start justify-between gap-3 p-3">
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{check.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{check.description}</p>
                      <p className="text-[10px] font-mono text-muted-foreground mt-1">{check.id}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runCheck(check.id)}
                      disabled={isRunning}
                      className="shrink-0"
                    >
                      {isRunning ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Play className="h-3 w-3 mr-1" />
                      )}
                      Run
                    </Button>
                  </div>

                  {result && style && (
                    <div className="p-3 space-y-3 border-t">
                      <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${style.className}`}>
                        <style.Icon className="h-4 w-4 shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium mr-2">{style.label}</span>
                          <span className="text-xs">{result.summary}</span>
                        </div>
                        <span className="text-[10px] font-mono opacity-70">
                          {result.duration_ms.toFixed(0)}ms
                        </span>
                      </div>

                      {Object.keys(result.metrics).length > 0 && (
                        <details className="border rounded">
                          <summary className="px-3 py-2 text-xs font-medium cursor-pointer hover:bg-muted/50">
                            Metrics
                          </summary>
                          <pre className="text-xs font-mono whitespace-pre-wrap break-all p-3 bg-slate-50 border-t max-h-96 overflow-auto">
                            {JSON.stringify(result.metrics, null, 2)}
                          </pre>
                        </details>
                      )}

                      {result.samples.length > 0 && (
                        <details className="border rounded" open>
                          <summary className="px-3 py-2 text-xs font-medium cursor-pointer hover:bg-muted/50">
                            Violation samples ({result.samples.length})
                          </summary>
                          <pre className="text-xs font-mono whitespace-pre-wrap break-all p-3 bg-slate-50 border-t max-h-96 overflow-auto">
                            {JSON.stringify(result.samples, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
